import { cn } from '@nwwp/ui-shadcn'
import { getReadinessTier, type ReadinessTier, TIER_LABEL } from './format'

const TIER_STROKE: Record<ReadinessTier, string> = {
  'on-track': 'var(--color-green-lighter)',
  almost: 'var(--color-yellow-light)',
  attention: 'var(--color-red)',
}

const R = 78
const CX = 100
const CY = 100
const ARC_LENGTH = R * Math.PI * 1.5
/** The arc is full at 140% so over-savers still see headroom above 100%. */
const FULL_AT = 1.4

function toXY(deg: number): { x: number; y: number } {
  const rad = ((deg - 90) * Math.PI) / 180
  return { x: CX + R * Math.cos(rad), y: CY + R * Math.sin(rad) }
}

const START = toXY(-135)
const END = toXY(135)
const ARC_PATH = `M ${START.x} ${START.y} A ${R} ${R} 0 1 1 ${END.x} ${END.y}`

interface GaugeSVGProps {
  ratio: number
  className?: string
  /** Blurs the percentage and tier label so a decoy ratio can't be read. */
  obscureLabels?: boolean
}

export function GaugeSVG({ ratio, className, obscureLabels = false }: GaugeSVGProps) {
  const tier = getReadinessTier(ratio)
  const fill = Math.min(Math.max(ratio, 0) / FULL_AT, 1)
  const displayPct = Math.round(Math.max(ratio, 0) * 100)

  return (
    <svg
      viewBox="0 0 200 150"
      role="img"
      aria-label={
        obscureLabels
          ? 'Sample of the retirement readiness gauge'
          : `Retirement readiness score: ${displayPct}%, ${TIER_LABEL[tier]}`
      }
      className={cn('block w-full', className)}
    >
      {obscureLabels && (
        <filter id="gauge-obscure" x="-20%" y="-50%" width="140%" height="200%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
      )}
      <path d={ARC_PATH} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="12" strokeLinecap="round" />
      <path
        d={ARC_PATH}
        fill="none"
        stroke={obscureLabels ? 'var(--color-green-lighter)' : TIER_STROKE[tier]}
        strokeWidth="12"
        strokeLinecap="round"
        strokeDasharray={ARC_LENGTH}
        strokeDashoffset={ARC_LENGTH * (1 - fill)}
        className="transition-[stroke-dashoffset,stroke] duration-700 ease-out motion-reduce:transition-none"
      />
      <g filter={obscureLabels ? 'url(#gauge-obscure)' : undefined}>
        <text
          x={CX}
          y={CY + 2}
          textAnchor="middle"
          style={{ fontSize: '40px', fontWeight: 300, fill: 'var(--color-white)', fontFamily: 'var(--font-display)' }}
        >
          {displayPct}%
        </text>
        <text
          x={CX}
          y={CY + 26}
          textAnchor="middle"
          style={{
            fontSize: '11px',
            fontWeight: 700,
            fill: 'var(--color-bone)',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          {TIER_LABEL[tier]}
        </text>
      </g>
    </svg>
  )
}
